import React, { useState } from 'react';

const ExtraStorageInput = ({ extraStorage, setExtraStorage, storagePriceTotal, STORAGE_PRICE_PER_UNIT, nvmePricePer50GB, hddPricePer50GB }) => {
  const [storageType, setStorageType] = useState('NVMe');

  const unitPrice = (type) => {
    if (type === 'NVMe') return nvmePricePer50GB ?? STORAGE_PRICE_PER_UNIT;
    return hddPricePer50GB ?? STORAGE_PRICE_PER_UNIT;
  };
  
  const handleStorageChange = (newAmountGB) => {
    // Ensure amount is in multiples of 50GB
    const amountInGB = Math.max(0, Math.floor(newAmountGB / 50) * 50);
    
    setExtraStorage({
      ...extraStorage,
      [storageType.toLowerCase()]: amountInGB
    });
  };

  const getCurrentStorage = () => {
    return storageType === 'NVMe' ? (extraStorage.nvme || 0) : (extraStorage.hdd || 0);
  };

  const getStorageUnits = () => {
    return getCurrentStorage() / 50; // Number of 50GB units
  };

  const calculateCurrentStorageCost = () => {
    const units = getStorageUnits();
    return units * unitPrice(storageType);
  };

  return (
    <div className="mb-6">
      <label className="block mb-2">
        Extra Storage (Optional)
        <span className="ml-2 text-xs text-gray-400">
          (NVMe GHS {unitPrice('NVMe').toFixed(2)} / SSD GHS {unitPrice('HDD').toFixed(2)} per 50GB)
        </span>
      </label>
      
      {/* Storage Type Selection */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setStorageType('NVMe')}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
            storageType === 'NVMe'
              ? 'bg-green-600 text-white'
              : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A]'
          }`}
        >
          NVMe (Faster)
        </button>
        <button
          type="button"
          onClick={() => setStorageType('HDD')}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
            storageType === 'HDD'
              ? 'bg-green-600 text-white'
              : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A]'
          }`}
        >
          SSD (Standard)
        </button>
      </div>
      
      {/* Storage Quantity Controls */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => handleStorageChange(getCurrentStorage() - 50)}
          className="w-10 h-10 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-xl font-bold"
        >
          −
        </button>
        <div className="flex-1 text-center">
          <span className="text-xl font-semibold">{getCurrentStorage()}</span>
          <span className="text-sm text-gray-400 ml-1">GB</span>
        </div>
        <button
          type="button"
          onClick={() => handleStorageChange(getCurrentStorage() + 50)}
          className="w-10 h-10 bg-[#033A6A] hover:bg-[#044A80] text-white rounded-lg text-xl font-bold"
        >
          +
        </button>
      </div>
      
      {/* Storage Info and Pricing */}
      {getCurrentStorage() > 0 && (
        <div className="mt-2">
          <p className="text-sm text-green-400">
            +GHS {calculateCurrentStorageCost().toFixed(2)}/month for extra {getCurrentStorage()}GB {storageType === 'HDD' ? 'SSD' : storageType}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {storageType === 'NVMe' 
              ? 'NVMe provides faster read/write speeds for high-performance applications'
              : 'SSD provides reliable storage at a lower cost'}
          </p>
        </div>
      )}
      
      {/* Total Extra Storage Display */}
      <div className="mt-3 text-xs text-gray-400">
        <p>Total extra storage cost: GHS {storagePriceTotal.toFixed(2)}/month</p>
      </div>
    </div>
  );
};

export default ExtraStorageInput;